import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const ProductCard = ({ product, priority = false }) => {
  const { addToCart } = useCart();
  
  // Find first valid size or default to medium
  const availableSizes = product?.sizes 
    ? ['small', 'medium', 'large'].filter(s => product.sizes[s] != null && product.sizes[s] > 0)
    : [];
  const hasSizes = availableSizes.length > 0;
  const initialSize = hasSizes ? availableSizes[0] : 'medium';

  const [selectedSize, setSelectedSize] = useState(initialSize);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const carouselImages = React.useMemo(() => {
    const images = [];
    if (product?.sizeImages) {
      if (product.sizeImages.small?.url) images.push({ url: product.sizeImages.small.url, sizeKey: 'small' });
      if (product.sizeImages.medium?.url) images.push({ url: product.sizeImages.medium.url, sizeKey: 'medium' });
      if (product.sizeImages.large?.url) images.push({ url: product.sizeImages.large.url, sizeKey: 'large' });
    }
    const existingUrls = new Set(images.map(img => img.url));
    if (product?.images) {
      for (const img of product.images) {
        if (!existingUrls.has(img.url)) {
          images.push({ ...img, sizeKey: null });
          existingUrls.add(img.url);
        }
      }
    }
    if (images.length === 0) {
      images.push({ url: 'https://via.placeholder.com/400x500?text=No+Image', sizeKey: null });
    }
    return images;
  }, [product]);

  const carouselLength = carouselImages.length;
  const safeIndex = currentImageIndex >= carouselLength ? 0 : currentImageIndex;

  const displayPrice = product.sizes?.[selectedSize] || product.price;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({ ...product, price: displayPrice }, 1, selectedSize);
  };

  const handleSizeSelect = (e, size) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedSize(size);
    let index = carouselImages.findIndex(img => img.sizeKey === size);
    
    // Fallback: If no explicit size image was found, map the size's index to the available images
    if (index === -1 && carouselImages.length > 1) {
      const sizeIndex = availableSizes.indexOf(size);
      if (sizeIndex !== -1 && sizeIndex < carouselImages.length) {
        index = sizeIndex;
      }
    }

    if (index !== -1) {
      setCurrentImageIndex(index);
    }
  };

  return (
    <Link to={`/product/${product._id}`} className="group h-full block">
      <div className="h-full flex flex-col bg-white rounded-xl overflow-hidden shadow-premium sm:hover:shadow-2xl transition-all duration-500 border border-gray-100/50">
        <div className="relative aspect-[4/5] overflow-hidden bg-gray-50 rounded-t-xl">
          <img
            src={carouselImages[safeIndex]?.url || 'https://via.placeholder.com/400x500?text=No+Image'}
            alt={product.name}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : 'auto'}
            decoding="async"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/400x500?text=Image+Error';
              if (e.target.src.includes('via.placeholder')) {
                e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
              }
            }}
            className="product-card-image" style={{ filter: 'brightness(1.04) saturate(1.05)' }}
          />

          {/* Luxury Badges - Reduced for Mobile */}
          <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex flex-col gap-1 sm:gap-2">
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="bg-rose-500/90 backdrop-blur-md text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-soft tracking-[0.1em] sm:tracking-[0.2em] uppercase font-sans-ui">
                -{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
              </span>
            )}
            {!product.inStock && (
              <span className="bg-gray-900/80 backdrop-blur-md text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-soft tracking-[0.1em] sm:tracking-[0.2em] uppercase font-sans-ui">
                Out of Stock
              </span>
            )}
          </div>
        </div>

        <div className="p-3 sm:p-4 flex flex-grow flex-col font-['Jost'] font-light">
          {/* Name - Now at the Top */}
          <h3 className="text-lg sm:text-xl md:text-2xl font-light text-gray-800 mb-2 group-hover:text-emerald-900 transition-colors line-clamp-1 leading-snug sm:leading-tight">
            {product.name}
          </h3>

          {/* Size Selector - Moved up below the Name */}
          {hasSizes && (
            <div className="flex flex-col gap-1 mb-3">
              <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">Size</span>
              <div className="flex gap-1.5">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    onClick={(e) => handleSizeSelect(e, size)}
                    className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-full border transition-all duration-300 ${selectedSize === size
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
                      : 'border-gray-100 text-gray-400 hover:border-emerald-200 hover:text-emerald-600'
                      }`}
                  >
                    {size.charAt(0).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto space-y-4 sm:space-y-5">
            {/* Price - Now on the Left */}
            <div className="text-left">
              <span className="text-xl sm:text-2xl font-normal text-emerald-600">
                {displayPrice.toLocaleString()}<span className="text-xs sm:text-sm font-light text-emerald-700/60 ml-1">ETB</span>
              </span>
            </div>

            {/* Premium Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock}
              className={`w-full py-2.5 sm:py-3 px-4 sm:px-5 rounded-full font-normal text-xs sm:text-sm tracking-widest uppercase transition-all duration-500 flex items-center justify-center gap-2 overflow-hidden active:scale-95 group/btn ${product.inStock
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md sm:shadow-lg hover:shadow-emerald-600/20'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-75'
                }`}
            >
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
              <span>{product.inStock ? 'Add to Cart' : 'Out of Stock'}</span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;

